import type {
  FormDefinitionCategoryCountsResponse,
  FormDefinitionDependenciesResponse,
  FormDefinitionRequest,
  FormDefinitionResponse,
  GetFormDefinitionFormFieldListRequest,
  GetFormDefinitionListRequest,
  GetFormDefinitionListRequestV2,
  GetListResponseOfFormDefinitionSimpleResponse,
  GetListResponseOfFormFieldSimpleResponse,
  GetListResponseOfSelectOption,
} from 'src/api/ProactApiClient';
import {formDefinitionApiClient} from 'src/api/ProactApiClientInstances';
import {
  formDefinitionCategoryCountsQueryFn,
  formDefinitionFlattenedQueryFn,
  formDefinitionListQueryFn,
  formDefinitionSelectOptionsQueryFn,
  formDefinitionQueryFn,
} from 'src/app/forms/offline/reads';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

export const formDefinitionApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getFormDefinitionSelectOptions: builder.query<
      GetListResponseOfSelectOption,
      GetFormDefinitionListRequest
    >({
      queryFn: formDefinitionSelectOptionsQueryFn(params =>
        formDefinitionApiClient.listSelectOptions(params)
      ),
      providesTags: ['FormDefinitions'],
    }),
    getFormDefinitionList: builder.query<
      GetListResponseOfFormDefinitionSimpleResponse,
      GetFormDefinitionListRequest
    >({
      queryFn: formDefinitionListQueryFn(params =>
        formDefinitionApiClient.list(params)
      ),
      providesTags: ['FormDefinitions'],
    }),
    getFormDefinitionCategoryCounts: builder.query<
      FormDefinitionCategoryCountsResponse,
      void
    >({
      queryFn: formDefinitionCategoryCountsQueryFn(() =>
        formDefinitionApiClient.listCategoryCounts()
      ),
      providesTags: ['FormDefinitions'],
    }),
    getFormDefinitionListV2: builder.query<
      GetListResponseOfFormDefinitionSimpleResponse,
      GetFormDefinitionListRequestV2
    >({
      queryFn: clientQuery(params => formDefinitionApiClient.listV2(params)),
      providesTags: ['FormDefinitions'],
    }),
    getFormDefinitionFieldList: builder.query<
      GetListResponseOfFormFieldSimpleResponse,
      GetFormDefinitionFormFieldListRequest
    >({
      queryFn: clientQuery(params =>
        formDefinitionApiClient.listFormFields(params)
      ),
      providesTags: ['FormDefinitions'],
    }),
    getFormDefinition: builder.query<FormDefinitionResponse, number>({
      queryFn: formDefinitionQueryFn(id => formDefinitionApiClient.get(id)),
      providesTags: ['FormDefinitions'],
    }),
    getFormDefinitionFlattened: builder.query<
      FormDefinitionResponse,
      {id: number; flattenSubform: boolean}
    >({
      queryFn: formDefinitionFlattenedQueryFn(({id, flattenSubform}) =>
        formDefinitionApiClient.getFlattened(id, flattenSubform)
      ),
      providesTags: ['FormDefinitions'],
    }),
    getFormDefinitionDependencies: builder.query<
      FormDefinitionDependenciesResponse,
      number
    >({
      queryFn: clientQuery(id => formDefinitionApiClient.getDependencies(id)),
      providesTags: ['FormDefinitions'],
    }),
    createFormDefinition: builder.mutation<
      FormDefinitionResponse,
      FormDefinitionRequest
    >({
      queryFn: clientQuery(request => formDefinitionApiClient.create(request)),
      invalidatesTags: ['FormDefinitions'],
    }),
    updateFormDefinition: builder.mutation<
      FormDefinitionResponse,
      FormDefinitionRequest
    >({
      queryFn: clientQuery(request => formDefinitionApiClient.update(request)),
      invalidatesTags: ['FormDefinitions'],
    }),
    deleteFormDefinition: builder.mutation<void, number>({
      queryFn: voidClientQuery(id => formDefinitionApiClient.delete(id)),
      invalidatesTags: ['FormDefinitions'],
    }),
    getFormDefinitionVersion: builder.query<
      FormDefinitionResponse,
      {id: number; version: number}
    >({
      queryFn: clientQuery(({id, version}) =>
        formDefinitionApiClient.getVersion(id, version)
      ),
      providesTags: ['FormDefinitions'],
    }),
    getFormDefinitionFlattenedVersion: builder.query<
      FormDefinitionResponse,
      {id: number; version: number; flattenSubform: boolean}
    >({
      queryFn: clientQuery(({id, version, flattenSubform}) =>
        formDefinitionApiClient.getFlattenedVersion(id, version, flattenSubform)
      ),
      providesTags: ['FormDefinitions'],
    }),
  }),
});

export const {
  useGetFormDefinitionListQuery,
  useGetFormDefinitionCategoryCountsQuery,
  useGetFormDefinitionListV2Query,
  useGetFormDefinitionFieldListQuery,
  useGetFormDefinitionSelectOptionsQuery,
  useGetFormDefinitionQuery,
  useLazyGetFormDefinitionQuery,
  useGetFormDefinitionFlattenedQuery,
  useLazyGetFormDefinitionFlattenedQuery,
  useGetFormDefinitionDependenciesQuery,
  useLazyGetFormDefinitionDependenciesQuery,
  useCreateFormDefinitionMutation,
  useUpdateFormDefinitionMutation,
  useDeleteFormDefinitionMutation,
  useGetFormDefinitionVersionQuery,
  useLazyGetFormDefinitionVersionQuery,
  useGetFormDefinitionFlattenedVersionQuery,
  useLazyGetFormDefinitionFlattenedVersionQuery,
} = formDefinitionApi;
